import axios from "axios";
import {toastr} from 'Helpers/toastr'
const LLAMA_TIMEOUT = 600000 // 10 minutes - should cover prompt processing time when no cache is used

export const axiosInstance = axios.create({})

function withSessionId(url){
  const arnoldSessionId = sessionStorage.getItem('arnoldSessionId')
  if (!arnoldSessionId) {
    // because this should logically never happen, but if it does, we want to reload the page
    console.error('withSessionId: arnoldSessionId not found in sessionStorage, initiating reload in 5 seconds')
    setTimeout(() => {
      console.log('withSessionId: reloading page')
      window.location.reload()
    })
    return
  }
  const u = new URL(url)
  u.searchParams.append('arnoldSessionId', arnoldSessionId)
  return u.toString()
}

export default class RequestAPI {
  constructor(serviceUrl) {
    this.serviceUrl = serviceUrl
  }

  getServiceUrl() {
    return this.serviceUrl
  }

  get = async (path, config, isSessionRequired = true) => {
    try {
      const response = await axiosInstance.get(
        isSessionRequired ?
          withSessionId(this.serviceUrl + path)
          :
          this.serviceUrl + path
        ,
        {headers: RequestAPI.getHeaders(), ...config})
      return response?.data
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message)
        }
      }
      throw (e)
    }
  }

  post = async (path, body, config) => {
    try {
      const response = await axiosInstance.post(withSessionId(this.serviceUrl + path), body, {headers: RequestAPI.getHeaders(), ...config})
      if (Object?.hasOwn(response?.data, 'isSuccess') && !response.data.isSuccess) {
        toastr.error(response.data?.messageDesc || response.data?.errorDescription || response.data?.errorMessage || 'שגיאת שרת כללית')
      }
      return response?.data
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message)
        }
      }
      throw (e)
    }
  }
  postWithFetch = async (path, body) => {
    try {
      console.debug('postWithFetch', {path, body, thisServiceUrl: this.serviceUrl})
      const response = await fetch(withSessionId(this.serviceUrl + path), { method: 'POST', body });
      return (await response.json() || await response.text())
    } catch (killerErr) {
      const e = JSON.parse(JSON.stringify(killerErr))
      if (e?.response?.status === 413) {
        console.error('413 error, file too large')
        return ''
      } else if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message)
        }
      }
      console.error(e)
      throw (e)
    }

  }

  postWithFormData = async (path, body, progressUpdates) => {
    const data = new FormData()
    Object.keys(body).forEach(key => data.append(key, typeof body[key] === 'object' && !(body[key] instanceof Blob) && !(body[key] instanceof File) ? JSON.stringify(body[key]) : body[key]))
    try {
      const response = await axiosInstance.post(withSessionId(this.serviceUrl + path), data, {
        headers: {
          ...RequestAPI.getHeaders(),
        },
        ...RequestAPI.progressUpdatesManager(progressUpdates),
        timeout: LLAMA_TIMEOUT,
      })
      return response?.data
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message, {autoClose:10000})
        }
      }
      throw (e)
    }
  }

  postWithStreamResponse = async (path, body, publishChunk = async () => {},endOfStream = async () => {}) => {
    const data = new FormData()
    Object.keys(body).forEach(key => data.append(key, typeof body[key] === 'object' && !(body[key] instanceof Blob) && !(body[key] instanceof File) ? JSON.stringify(body[key]) : body[key]))
    try {
      const response = await fetch(withSessionId(this.serviceUrl + path), {
        method: 'POST',
        body: data,
        headers: {
          ...RequestAPI.getHeaders(),
        },
        timeout: LLAMA_TIMEOUT,
      })

      if (!response?.ok) {
        console.error('postWithStreamResponse: response status not 200', {response})
        const errorText = await response.text()
        const error = new Error(`Error in postWithStreamResponse: ${errorText}`)
        error.code = response.status
        throw error
      }
      const splittedStream = await response.body
      const reader = splittedStream.getReader()

      let responseData = ''
      let isDone = false
      let statusCodeOverride
      while (!isDone) {
        const readerChunk = await reader.read()
        isDone = readerChunk.done
        let chunk = new TextDecoder().decode(readerChunk.value)
        const statusCodeOverrideInfo = chunk.match(/^(.*)statusCodeOverride=(\d+)(.*)$/)
        if (statusCodeOverrideInfo) {
          const before = statusCodeOverrideInfo[1]
          statusCodeOverride = statusCodeOverrideInfo[2]
          const after = statusCodeOverrideInfo[3]
          chunk = before+after
        }
        responseData += chunk
        await publishChunk(chunk,statusCodeOverride)
        if (statusCodeOverride) {
          const ex = new Error(chunk)
          ex.code = statusCodeOverride
          throw ex
        }
      }
      return responseData
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message, {autoClose:10000})
        }
      }
      throw (e)
    } finally {
      await endOfStream()
    }
  }

  put = async (path, body) => {
    try {
      const response = await axiosInstance.put(withSessionId(this.serviceUrl + path), body, {headers: RequestAPI.getHeaders()})
      return response?.data
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message)
        }
      }
      throw (e)
    }
  }

  delete = async (path, body) => {
    try {
      const response = await axiosInstance.delete(this.serviceUrl + path, {headers: RequestAPI.getHeaders(), data: body})
      return response?.data
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 401) {
        // store.dispatch(disconnect())
      } else {
        if (e?.response?.data || e?.message) {
          toastr.error(e?.response?.data || e.message)
        }
      }
      throw (e)
    }
  }

  static getHeaders() {
    return { }
  }

  static progressUpdatesManager(progressUpdates) {
    return progressUpdates ? {
      onUploadProgress: progressUpdates,
    } : {}
  }
}
